import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";
import MenuLateral from '../Components/MenuPrincipal/MenuLateral';
import GradesGrid from '../Components/GradesGrid/GradesGrid';
import Pagination from '../Components/Pagination/Pagination'; // Ensure this matches path
import './Grades.css'; // Use the new CSS file

function Grades() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();
    const [grades, setGrades] = useState([]); // Filtered list
    const [allGrades, setAllGrades] = useState([]); // Full list
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Verifica permissão de leitura
    useEffect(() => {
        if (appUserData && !appUserData['ler_grades']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    // Fetch ALL Grades
    useEffect(() => {
        const fetchGrades = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const url = new URL(`${API_URL}/api/grades`);

                // Client-side filtering: Fetch ALL
                // url.searchParams.append('search', textoPesquisa); // REMOVED

                const response = await fetch(url.toString(), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error("Falha ao carregar as grades.");

                const data = await response.json();
                setAllGrades(data);
                setGrades(data); // Initialize filtered with full data

            } catch (error) {
                console.error("Erro ao buscar grades: ", error);
                Swal.fire("Erro", "Não foi possível carregar as grades.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchGrades();
    }, [currentUser, trigger]);

    // Filter Logic
    useEffect(() => {
        if (!textoPesquisa) {
            setGrades(allGrades);
        } else {
            const lowerSearch = textoPesquisa.toLowerCase();
            const filtered = allGrades.filter(grade =>
                (grade.descricao && grade.descricao.toLowerCase().includes(lowerSearch))
            );
            setGrades(filtered);
        }
        setCurrentPage(1);
    }, [textoPesquisa, allGrades]);

    // Debounce Search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setTextoPesquisa(pesquisa);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [pesquisa]);

    const handlePesquisar = () => {
        setTextoPesquisa(pesquisa);
    };

    const handleAtualizar = () => {
        setPesquisa('');
        setTextoPesquisa('');
    };

    async function deleteGrade(id) {
        // Validation for Delete
        if (appUserData && appUserData['excluir_grades'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        Swal.fire({
            title: "Você tem certeza?",
            text: "Esta ação excluirá a grade e todas as suas variações!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#DC3545",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = await currentUser.getIdToken();
                    const API_URL = process.env.REACT_APP_API_URL;
                    const response = await fetch(`${API_URL}/api/grades/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || "Erro ao deletar");
                    }
                    setTrigger(Date.now());
                    Swal.fire("Excluído!", "A grade foi deletada com sucesso.", "success");
                } catch (error) {
                    Swal.fire("Erro!", error.message, "error");
                }
            }
        });
    }

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentGrades = grades.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const changeItemsPerPage = (limit) => {
        setItemsPerPage(limit);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="grades-page-container" style={{ display: 'flex' }}>
                <MenuLateral />
                <div className="d-flex justify-content-center align-items-center w-100" style={{ height: '100vh', marginTop: '60px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grades-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="grades-header">
                    <h1 className="grades-title">
                        <i className="fas fa-th text-primary"></i>
                        Listagem de <span className="highlight">Grades</span>
                    </h1>
                </div>

                <main className="grades-content">
                    <div className="grades-controls">
                        <Link
                            to="/app/novagrade"
                            className="btn-primary-custom me-2"
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_grades'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i> Nova Grade
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por descrição..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                            />
                        </div>
                        <button className="btn-icon-custom" onClick={handleAtualizar} disabled={loading} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    <div className="grades-grid-container px-0">
                        <GradesGrid arrayGrades={currentGrades} clickDelete={deleteGrade} userPermissions={appUserData} />
                    </div>
                </main>
            </div>

            <Pagination
                totalItems={grades.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={paginate}
                onItemsPerPageChange={changeItemsPerPage}
                loading={loading}
            />
        </div>
    );
}

export default Grades;